import { IGraphicSize, ILinkPreviewModel } from '@gem/models/commonModel';
import { PayloadType } from '@gem/models/messageModel';
import { IUserDisplayData } from '@gem/models/userModel';

import { StickerType } from 'APP/model/stickers/stickersModel.types';
import { OneOfType } from 'APP/types/utility';

import { GroupType, IPersonalizedGroup } from '../group/groupModel.types';
import { FinishReasons } from '../mainCall/mainCallModel.types';

export { PayloadType };

export enum MessageModelErrorCode {
  ArticleIsNotAllowed = 'articleIsNotAllowed',
  NoPermissionsToSendMessage = 'noPermissionsToSendMessage',
  HasNoPermissionsToUpdateMessage = 'hasNoPermissionsToUpdateMessage',
  MaxAlbumItemsLimitReached = 'maxAlbumItemsLimitReached',
  MessageDoesNotExist = 'messageDoesNotExist',
  ReactOnSystemMessageIsForbidden = 'reactOnSystemMessageIsForbidden',
  QuotedMessageIsNotExist = 'quotedMessageIsNotExist',
  UnableToCreateLinkPreview = 'unableToCreateLinkPreview',
  SecretMessageCommitSyncRequired = 'secretMessageCommitSyncRequired',
}

export enum MediaType {
  Audio = 'AudioMessage',
  Album = 'Album',
  Article = 'Article',
  Link = 'Link',
  File = 'File',
  Image = 'Image',
  Video = 'Video',
  Voice = 'VoiceMessage',
}

export enum GetMessagesDirection {
  Newest = 'NEWEST',
  Oldest = 'OLDEST',
}

export enum SendMessageStatus {
  Failed = 'failed',
  Seen = 'seen',
  Sending = 'sending',
  Sent = 'sent',
}

export enum MessageKeyWordType {
  Congratulation = 'CONGRATULATION',
  NewYear = 'NEW_YEAR',
}

export enum VoiceTranscriptionStatus {
  NotTranscribed = 'NOT_TRANSCRIBED',
  InProgress = 'IN_PROGRESS',
  TranscriptionSuccessful = 'TRANSCRIPTION_SUCCESSFUL',
  TranscriptionFailed = 'TRANSCRIPTION_FAILED',
}

export interface IPayloadRequestData {
  payloadType: PayloadType;
}

export interface IForwardMessage {
  from: string;
  groupId?: string;
  messageId: string;
  senderId: string;
  clientGroupSourceId?: string;
}

export interface IUpdateMessageReaction {
  groupId: string;
  messageId: string;
  reactionId: string;
}

export interface IRepliedMessageData {
  id: string;
  payload: IMessagePayload;
  sender: string;
  senderId: string;
  groupId: string;
}

export interface IQuoteUserSource {
  userId: string;
}

export interface IQuoteChatSource {
  chatId: string;
  messageId: string;
  title?: string;
}

export interface IQuoteUserChatSource {
  user: IQuoteUserSource;
  chat: IQuoteChatSource;
}

export interface IQuoteUnknownSource {
  title: string;
  //  userId, chatId, messageId - present only on the FE side
  //  used to create and display correct Quote before sending data to BE
  userId?: string;
  chatId?: string;
  messageId?: string;
}

export type TQuoteSource = OneOfType<{
  user: IQuoteUserSource;
  chat: IQuoteChatSource;
  userChat: IQuoteUserChatSource;
  unknown: IQuoteUnknownSource;
}>;

export interface IQuote {
  source: TQuoteSource;
  text: string;
}

export interface IUserReactions {
  currentUserReactionId?: string;
  usersReactions: { [key: string]: number };
}

export interface IMessageKeyWord {
  keyWord: string;
  keyWordType: MessageKeyWordType;
}

//ToDo change serverTime, editTime and clientTime to number/ and use mapMessageResponse
export interface IMessage {
  clientTime: string;
  clientUuid: string;
  deepLink?: string;
  editTime?: string;
  forwardedMessage?: IForwardMessage;
  groupId: string;
  id: string;
  links: string[];
  payload: IMessagePayload;
  repliedMessage?: IRepliedMessageData;
  quote?: IQuote;
  usersReactions: IUserReactions;
  senderId: string;
  serverTime: string;
  threadId?: string;
  views: string;
}

export interface IMessageWithCommentCount {
  commentCount: number;
  message: IMessage;
}

export interface IReachTextMessagePayloadData {
  text: string;
  linkPreview?: string;
  linkPreviewCancelled?: boolean;
}

export interface IArticlePayloadData extends IPayloadRequestData {
  title: string;
  payloads: IOrderedItemMessagePayload[];
}

export interface IForwardRequest {
  sourceGroupId: string;
  messagesToForward: {
    sourceMessageId: string;
    clientTs: number;
    clientUuid: string;
    targetGroupId?: string;
    targetContactId?: string;
    payloadIds?: string[];
  }[];
}

export interface IForwardMessageResponse {
  messages: IMessage[];
  groupId: string;
  lastMessageTs: string;
}
export interface IForwardMessagesResponse {
  forwardedMessages: IForwardMessageResponse[];
  unavailableGroups: string[];
  unavailableContacts: string[];
}

export interface IDeleteMessagesRequest {
  mode: DeleteMessagesMode;
  groupId: string;
  messageIds: Array<string>;
}

export enum DeleteMessagesMode {
  All = 'FOR_ALL',
  Me = 'FOR_ME',
}

export interface IDeleteScheduledMessagesRequest {
  groupId: string;
  messageIds: Array<string>;
}

export interface IMessagesResponse {
  messages: IMessage[];
  groups: IPersonalizedGroup[];
  users: IUserDisplayData[];
}

export interface IGetMessagesResponse {
  messages: IMessage[];
  cursorTs?: string;
  maxTs?: string;
  minTs?: string;
}

export interface IGetScheduledMessagesResponse {
  messages: IScheduledMessage[];
  nextCursor?: string;
}

export interface IScheduledMessage {
  repliedMessage?: IRepliedMessageData;
  payload: IMessagePayload;
  id: string;
  groupId: string;
  clientUuid: string;
  delay: string;
  serverTime: string;
  clientTime: string;
  status?: string;
}

export interface ISendNewMessageRequest {
  clientTime: number;
  groupId: string;
  clientUuid: string;
  retry?: boolean;
  quotedMessageId?: string;
  quotedMessageData?: IMessageRequestQuotedMessageData;
  payload: IUpdateMessageRequestPayloadData & IRequestData;
  id?: string;
}

export interface ISendMessageResponse {
  message: IMessage;
  previousMessageTs: number;
}

export interface IUserMention {
  userId: string;
  nickname: string;
}

export interface IGetMessageRequest {
  groupId: string;
  messageId: string;
  halfLimit?: number;
}

export type ISubscribeUpdateMessagesRequest = ISubscribeUpdateMessagesRequestItem[];

interface ISubscribeUpdateMessagesRequestItem {
  messageId: string;
  groupId: string;
}

export type IMessageDeleteEvent = ISubscribeUpdateMessagesRequestItem;

export interface IGetUsersViewedMessageRequest {
  cursor?: string;
  groupId: string;
  messageId: string;
  limit?: number;
}

export interface IGetUsersViewedMessageResponse {
  userIds: string[];
  cursor?: string;
}

export interface ITranslateMessageRequest {
  groupId: string;
  messageId: string;
  targetLang: string;
}

export interface IGetMessageDraftsRequest {
  groupId: string;
  cursorTs?: string;
}

export interface IGetMessageDraftRequest {
  groupId: string;
  cursorTs?: string;
}

export interface IMessageDraftsResponse {
  payload: IMessagePayload;
  id: string;
  groupId: string;
  senderId: string;
  clientUuid: string;
  serverTime: string;
  clientTime: string;
}

export interface IMessageDraftsAndTsResponse {
  drafts: IMessageDraftsResponse[];
  nextTs?: string;
}

export interface ISaveMessageDraftRequest {
  payload: IMessagePayload;
  groupId: string;
  clientTime: string;
  clientUuid: string;
}

export interface IUpdateMessageDraftRequest {
  payload: IMessagePayload;
  groupId: string;
  draftId: string;
}

export interface IDeleteMessageDraftsRequest {
  groupId: string;
  draftIds: Array<string>;
}

export interface IUpdateScheduledMessageRequest {
  payload: IMessagePayload;
  groupId: string;
  messageId: string;
  clientTime: string;
  delay: string;
  clientId: string;
  quotedMessageData?: IMessageRequestQuotedMessageData;
}

export interface ISendScheduledMessageRequest {
  payload: IMessagePayload;
  groupId: string;
  clientTime: number;
  delay: number;
  clientUuid: string;
  quotedMessageData?: IMessageRequestQuotedMessageData;
}

export interface IGetMessagesByMediaTypesRequest {
  mediaTypes: MediaType[];
  direction: GetMessagesDirection;
  groupId: string;
  cursorTs?: string;
}

export interface IGetMessagesByMediaTypesCountRequest {
  mediaTypes: MediaType[];
  groupId: string;
}

export type IGetMessagesByMediaTypesCountResponse = Record<MediaType, number>;

export interface IGetMessagesRequest {
  groupId: string;
  direction: GetMessagesDirection;
  cursorTs?: string;
  count?: number;
}

/* MESSEGE PAYLOAD */

export interface IOrderedItemMessagePayload extends IPayloadRequestData {
  id: string;
  order: number;
  payload: IMessagePayload;
}

export interface IAdvertisementPayload extends IPayloadRequestData {
  comment: string;
  buttonCaption?: string;
  buttonUrl?: string;
  payloads: IOrderedItemMessagePayload[];
}

export interface IAlbumMessagePayload extends IPayloadRequestData {
  comment?: string;
  payloads: IOrderedItemMessagePayload[];
  userMentions: IUserMention[];
  keyWord?: IMessageKeyWord;
}

export interface IAudioMessagePayload extends IPayloadRequestData {
  fileName: string;
  fileSize: string;
  url: string;
  mimeType: string;
  comment: string;
  userMentions: IUserMention[];
  keyWord?: IMessageKeyWord;
  artist?: string;
  title?: string;
  duration: string;
}

export interface IFileMessagePayload extends IPayloadRequestData {
  fileName: string;
  fileSize: string;
  url: string;
  mimeType: string;
  comment?: string;
  userMentions: IUserMention[];
  keyWord?: IMessageKeyWord;
}

export interface IImageMessagePayload extends IPayloadRequestData {
  fileName: string;
  fileSize: string;
  url: string;
  mimeType: string;
  comment?: string;
  userMentions: IUserMention[];
  graphicSize: IGraphicSize;
  stretchedByWidth: boolean;
  keyWord?: IMessageKeyWord;
}

export interface IStickerMessagePayload extends IPayloadRequestData {
  id: string;
  stickerPackId?: string;
  name: string;
  stickerUrl: string;
  type: StickerType;
  animationUrl?: string;
  graphicSize: IGraphicSize;
}

export interface ITextMessagePayload extends IPayloadRequestData {
  text: string;
  userMentions: IUserMention[];
  linkPreview?: ILinkPreviewModel;
  keyWord?: IMessageKeyWord;
}

export interface IE2eeMessagePayload extends IPayloadRequestData {
  e2eePayload: ArrayBuffer;
  epoch: number;
  senderDeviceId: string;
  selfPayload?: ArrayBuffer;
}

export interface IVideoMessagePayload extends IPayloadRequestData {
  fileName: string;
  fileSize: string;
  url: string;
  mimeType: string;
  comment?: string;
  userMentions: IUserMention[];
  graphicSize: IGraphicSize;
  previewUrl?: string;
  keyWord?: IMessageKeyWord;
}

export interface IVoiceMessagePayload extends IPayloadRequestData {
  fileName: string;
  fileSize: string;
  url: string;
  mimeType: string;
  comment?: string;
  userMentions: IUserMention[];
  keyWord?: IMessageKeyWord;
  duration: string;
  histogram: string;
  transcriptionText?: string;
  transcriptionStatus: VoiceTranscriptionStatus;
}

export interface ISystemMessagePayload extends IPayloadRequestData {
  initiator: string;
  initiatorNickName?: string;
}

export interface ISystemMessageCallPayload extends ISystemMessagePayload {
  channelId: string;
  startTs: string;
  finishTs?: string;
  duration?: string;
  finishReason?: FinishReasons;
  finishInitiator?: string;
  finishInitiatorNickname?: string;
}

export interface ISystemMessageChatNameChangedPayload extends ISystemMessagePayload {
  oldName: string;
  newName: string;
}

export interface ISystemMessageChatTypeChangedPayload extends ISystemMessagePayload {
  newType: GroupType.ChannelClosed | GroupType.ChannelOpen | GroupType.Closed | GroupType.Open;
}

export interface IUserWithDisplayedName {
  id: string;
  displayedName: string;
}

export interface ISystemMessageUsersChangedPayload extends ISystemMessagePayload {
  users: IUserWithDisplayedName[];
}

export interface ISystemMessageChatTtlChangedPayload extends ISystemMessagePayload {
  periodMs: number;
}

export type IUnsupportedPayload = IPayloadRequestData;

export type IMessagePayload =
  | IAdvertisementPayload
  | IAlbumMessagePayload
  | IAudioMessagePayload
  | IFileMessagePayload
  | IImageMessagePayload
  | IStickerMessagePayload
  | ITextMessagePayload
  | IVideoMessagePayload
  | IVoiceMessagePayload
  | IOrderedItemMessagePayload
  | ISystemMessagePayload
  | ISystemMessageCallPayload
  | ISystemMessageChatNameChangedPayload
  | ISystemMessageChatTypeChangedPayload
  | ISystemMessageUsersChangedPayload
  | IUnsupportedPayload;

export interface IRequestData {
  payloadType: PayloadType;
}

export interface IOrderedItemRequestData {
  id: string;
  order: number;
  payload: IRequestData;
}

export interface IAlbumRequestData extends IRequestData {
  payloads: Array<IOrderedItemRequestData>;
  comment?: string;
}

export interface IArticleRequestData extends IRequestData {
  title: string;
  payloads: Array<IOrderedItemRequestData>;
}

export interface IAudioRequestData extends IRequestData {
  url: string;
  comment?: string;
  artist?: string;
  title?: string;
  duration: string;
}

export interface IButtonPayload {
  type: string;
}

export interface IButtonApiModel {
  id: string;
  title: string;
  payload?: IButtonPayload;
}

export interface IButtonSelectedRequestData extends IRequestData {
  button: IButtonApiModel;
}

export interface IButtonsRq extends IRequestData {
  text: string;
  imageUrl?: string;
  graphicSize?: IGraphicSize;
  buttons: IButtonApiModel[];
  layout: string[];
}

export interface IContactRequestData extends IRequestData {
  phone?: string;
  userId?: string;
  name?: string;
}

export interface IFileRequestData extends IRequestData {
  url: string;
  comment?: string;
}

export interface IImageRequestData extends IRequestData {
  url: string;
  comment?: string;
}

export interface IInviteRequestData extends IRequestData {
  groupId: string;
}

export interface ILocationRequestData extends IRequestData {
  latitude: number;
  longitude: number;
}

export interface IStickerRequestData extends IRequestData {
  id: string;
  stickerPackId: string;
}

export interface ITextRequestData extends IRequestData {
  text: string;
  payload?: string;
  linkPreview?: ILinkPreviewModel;
  linkPreviewCancelled?: boolean;
}

export interface IE2eeRequestData extends IRequestData {
  epoch: number;
  senderDeviceId: string;
  e2eePayload: ArrayBuffer;
  selfPayload: ArrayBuffer;
  groupId: string;
}

export interface IVideoRequestData extends IRequestData {
  url: string;
  comment?: string;
  previewUrl: string;
  graphicSize?: IGraphicSize;
}

export interface IVoiceRequestData extends IRequestData {
  url: string;
  comment?: string;
  duration: string;
  histogram: string;
}

export type IUpdateMessageRequestPayload =
  | IAlbumRequestData
  | IArticleRequestData
  | IAudioRequestData
  | IButtonSelectedRequestData
  | IButtonsRq
  | IContactRequestData
  | IFileRequestData
  | IImageRequestData
  | IInviteRequestData
  | ILocationRequestData
  | IOrderedItemRequestData
  | IStickerRequestData
  | ITextRequestData
  | IVideoRequestData
  | IVoiceRequestData;

export interface IUpdateMessageRequestPayloadData {
  payload: IUpdateMessageRequestPayload & IRequestData;
}

export interface IUpdateMessageRequest {
  groupId: string;
  messageId: string;
  payload: IUpdateMessageRequestPayload & IRequestData;
}

export interface IMessageRequestQuotedMessageData {
  text?: string;
  sourceMessageId: string;
  sourceChatId: string;
}

/* MESSEGE PAYLOAD END */
